import { useEffect, useId, useMemo, useRef, useState } from "react";
import SystemIcon from "./SystemIcon";

const defaultGetKey = (option) => option?.id;
const defaultGetLabel = (option) => option?.label || "";
const defaultGetMeta = () => "";
const SIZE_ALIASES = { sm: "small", md: "medium", lg: "large" };
const ICON_SIZE = { small: "xsmall", medium: "xsmall", large: "xsmall" };

export default function SearchSelect({
  id,
  value,
  onChange,
  options = [],
  getKey = defaultGetKey,
  getLabel = defaultGetLabel,
  getMeta = defaultGetMeta,
  placeholder = "선택",
  searchPlaceholder = "검색",
  emptyText = "검색 결과가 없습니다.",
  disabled = false,
  size = "medium",
  className = "",
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const normalizedSize = SIZE_ALIASES[size] || size;
  const selected = options.find((option) => String(getKey(option)) === String(value));
  const filtered = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase("ko-KR");
    if (!keyword) return options.slice(0, 100);
    return options.filter((option) => (
      `${getLabel(option)} ${getMeta(option) || ""}`.toLocaleLowerCase("ko-KR").includes(keyword)
    )).slice(0, 100);
  }, [getLabel, getMeta, options, query]);

  useEffect(() => {
    setHighlightedIndex(filtered.length ? 0 : -1);
  }, [filtered]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        window.requestAnimationFrame(() => triggerRef.current?.focus());
      }
    };
    window.addEventListener("mousedown", closeOutside);
    window.addEventListener("keydown", closeOnEscape);
    window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      window.removeEventListener("mousedown", closeOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  function toggle() {
    if (disabled) return;
    setQuery("");
    setOpen((current) => !current);
  }

  function selectOption(option) {
    onChange(String(getKey(option)), option);
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function handleSearchKeyDown(event) {
    if (!filtered.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((current) => (current + 1) % filtered.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((current) => (current <= 0 ? filtered.length - 1 : current - 1));
    } else if (event.key === "Enter" && highlightedIndex >= 0) {
      event.preventDefault();
      selectOption(filtered[highlightedIndex]);
    }
  }

  return (
    <div
      className={`ds-search-select ds-search-select--${normalizedSize}${open ? " is-open" : ""}${className ? ` ${className}` : ""}`}
      ref={rootRef}
    >
      <button
        ref={triggerRef}
        id={id}
        className="ds-search-select-trigger"
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onKeyDown={(event) => {
          if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !open) {
            event.preventDefault();
            setQuery("");
            setOpen(true);
          }
        }}
      >
        <span className={selected ? "" : "is-placeholder"}>{selected ? getLabel(selected) : placeholder}</span>
        <SystemIcon
          name="angle"
          size={ICON_SIZE[normalizedSize] || "medium"}
          direction={open ? "up" : undefined}
          className="ds-search-select-chevron"
        />
      </button>
      {open && (
        <div className="ds-search-select-menu">
          <input
            ref={searchRef}
            className="ds-input ds-input--medium ds-search-select-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined}
            onKeyDown={handleSearchKeyDown}
          />
          <div id={listboxId} className="ds-search-select-options" role="listbox" aria-label={searchPlaceholder}>
            {filtered.map((option, index) => {
              const key = getKey(option);
              const active = String(key) === String(value);
              const highlighted = index === highlightedIndex;
              return (
                <button
                  key={String(key)}
                  id={`${listboxId}-option-${index}`}
                  className={`ds-search-select-option${active ? " is-selected" : ""}${highlighted ? " is-highlighted" : ""}`}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => selectOption(option)}
                >
                  <strong>{getLabel(option)}</strong>
                  {getMeta(option) && <span>{getMeta(option)}</span>}
                </button>
              );
            })}
            {!filtered.length && <div className="ds-search-select-empty">{emptyText}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
